import { api } from "@/api/utils";
import {
  ExtendMemberExpirationBody,
  GetFormSchemaForMemberAdminRequest,
  GetTransactionsForMemberResponse,
  MemberSearchResult,
  SetMemberBankAccountRequest,
  UpdateMemberEmailBody,
  UpdateMemberNoteBody,
} from "@givenwell/management-api";
import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";

export const searchMembersQuery = (searchTerm: string) =>
  queryOptions({
    queryKey: ["members", "search", { searchTerm: searchTerm }],
    queryFn: async () => {
      let members: MemberSearchResult[] = [];

      if (searchTerm) {
        const response = await api.member.searchMembers({ query: searchTerm });
        members = response.members;
      }

      return {
        members,
        query: searchTerm,
      };
    },
  });

export const memberWalletBalanceQuery = (memberId: string) =>
  queryOptions({
    queryKey: ["members", "wallet-balance", memberId],
    queryFn: async () => {
      const response = await api.member.getMemberWalletBalance({ memberId });
      return response;
    },
  });

export const memberActiveSubscriptionsQuery = (memberId: string) =>
  queryOptions({
    queryKey: ["members", "active-subscriptions", memberId],
    queryFn: async () => {
      const response = await api.member.getMemberActiveSubscriptions({ memberId });
      return response;
    },
  });

export const memberLocationQuery = (memberId: string) =>
  queryOptions({
    queryKey: ["members", "location", memberId],
    queryFn: async () => {
      const response = await api.member.getMemberLocation({ memberId });
      return response;
    },
  });

export const memberPurchaseHistoryQuery = (memberId: string) =>
  queryOptions({
    queryKey: ["members", "purchase-history", memberId],
    queryFn: async () => {
      const response = await api.member.getMemberPurchaseHistory({ memberId, page: 1, pageSize: 1000 });
      return response;
    },
  });

export const memberJoinDateQuery = (memberId: string) =>
  queryOptions({
    queryKey: ["member", memberId, "join-date"],
    queryFn: async () => {
      const response = await api.member.getMemberJoinDate({ memberId });
      return response;
    },
  });

export function useUpdateMemberEmailMutation() {
  return useMutation({
    mutationFn: async (args: { memberId: string; requestBody: UpdateMemberEmailBody }) => {
      return await api.member.updateMemberEmail(args);
    },
  });
}

export function memberNoteQuery(memberId: string) {
  return queryOptions({
    queryKey: ["member", memberId, "note"],
    queryFn: async () => await api.member.getMemberNote({ memberId }),
  });
}

export function memberRecentLoginsQuery(memberId: string) {
  return queryOptions({
    queryKey: ["member", memberId, "recent-logins"],
    queryFn: async () => {
      const response = await api.member.getMemberRecentLogins({ memberId });
      return response;
    },
  });
}

export function memberBankAccountQuery(memberId: string) {
  return queryOptions({
    queryKey: ["member", memberId, "bank-details"],
    queryFn: async () => {
      const response = await api.member.getMemberBankAccount({ memberId });
      return response;
    },
  });
}

export function memberPhoneNumbersQuery(memberId: string) {
  return queryOptions({
    queryKey: ["member", memberId, "phone-numbers"],
    queryFn: async () => {
      const response = await api.member.getMemberPhoneNumbers({ memberId });
      return response;
    },
  });
}

// -------------------------------------------------------------------------------------------------

export function useUpdateMemberNoteMutation(memberId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateMemberNoteBody) => api.member.updateMemberNote({ memberId, requestBody: body }),
    onSuccess: () => client.invalidateQueries(memberNoteQuery(memberId)),
  });
}

export const memberTransactionsQuery = (memberId: string, page: number = 1, pageSize: number = 50) =>
  queryOptions({
    queryKey: ["member", memberId, "transactions", { page, pageSize }],
    queryFn: async () => {
      const response = await api.member.getAdminTransactionsForMember({ memberId, pageNum: page, pageSize });
      return response;
    },
  });

export const memberTransactionsInfiniteQuery = (memberId: string, pageSize: number = 25) =>
  infiniteQueryOptions({
    queryKey: ["member", memberId, "transactions-infinite", { pageSize }] as const,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const response = await api.member.getAdminTransactionsForMember({
        memberId,
        pageNum: pageParam,
        pageSize,
      });
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: GetTransactionsForMemberResponse, _allPages, lastPageParam: number) => {
      if (!lastPage.has_more) return undefined;
      return lastPageParam + 1;
    },
  });

export const memberExpirationsQuery = (memberId: string) =>
  queryOptions({
    queryKey: ["member", memberId, "expirations"],
    queryFn: async () => {
      const response = await api.member.getMemberExpirations({ memberId });
      return response.expirations;
    },
  });

export function useExtendMemberExpirationMutation(memberId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (args: { expirationId: string; requestBody: ExtendMemberExpirationBody }) =>
      api.member.extendMemberExpiration({ memberId, ...args }),
    onSuccess: () => client.invalidateQueries(memberExpirationsQuery(memberId)),
  });
}

export const memberEapEntitlementsQuery = (memberId: string) =>
  queryOptions({
    queryKey: ["member", memberId, "eap-entitlements"],
    queryFn: async () => {
      const response = await api.member.getMemberEapEntitlements({ memberId });
      return response;
    },
  });

// -------------------------------------------------------------------------------------------------
// Bank account management (Admin only)

export const memberBankCountriesQuery = () =>
  queryOptions({
    queryKey: ["member", "bank-countries"],
    queryFn: async () => {
      const response = await api.member.getBankCountriesAdmin();
      return response;
    },
  });

export const memberFormSchemaQuery = (args: GetFormSchemaForMemberAdminRequest) =>
  queryOptions({
    queryKey: ["member", "form-schema", args],
    queryFn: () => api.member.getFormSchemaForMemberAdmin({ requestBody: args }),
    placeholderData: prev => prev,
  });

export function useSetMemberBankAccountMutation(memberId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (args: Omit<SetMemberBankAccountRequest, "member_id">) =>
      api.member.setMemberBankAccount({
        memberId,
        requestBody: { ...args, member_id: memberId },
      }),
    onSuccess: () => client.invalidateQueries(memberBankAccountQuery(memberId)),
  });
}
